import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import {
  ArrowLeft, Smartphone, ScanLine, Store, Landmark, Banknote,
  ChevronDown, QrCode, HandCoins, Cpu,
} from "lucide-react";

const GOLD = "#eec643";
const VIOLET = "#7a5af8";

/* ---------------- particle field (pure CSS) ---------------- */
const Particles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    {Array.from({ length: 36 }).map((_, i) => (
      <span
        key={i}
        className="absolute rounded-full"
        style={{
          width: i % 3 === 0 ? 3 : 2,
          height: i % 3 === 0 ? 3 : 2,
          left: `${(i * 37) % 100}%`,
          top: `${(i * 53) % 100}%`,
          background: i % 4 === 0 ? VIOLET : GOLD,
          opacity: 0.25 + (i % 5) * 0.12,
          animation: `guide-float ${7 + (i % 6)}s ease-in-out ${i * 0.35}s infinite`,
        }}
      />
    ))}
    <style>{`@keyframes guide-float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-46px) } }`}</style>
  </div>
);

/* ---------------- kinetic headline (word-by-word) ---------------- */
const Kinetic = ({ words, className = "", delay = 0 }) => (
  <span className={className}>
    {words.split(" ").map((w, i) => (
      <motion.span
        key={i}
        className="inline-block mr-[0.28em]"
        initial={{ opacity: 0, y: 26, rotateX: -70 }}
        whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ delay: delay + i * 0.08, duration: 0.55, ease: [0.2, 1.4, 0.4, 1] }}
      >
        {w}
      </motion.span>
    ))}
  </span>
);

/* ---------------- 3D screenshot slab ---------------- */
const Slab = ({ src, alt, caption, tilt = -6 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const raw = useTransform(scrollYProgress, [0, 1], [tilt, -tilt]);
  const rotateX = useSpring(raw, { stiffness: 60, damping: 16 });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  return (
    <motion.figure
      ref={ref}
      data-testid={`slab-${alt.replace(/\s+/g, "-").toLowerCase()}`}
      className="relative"
      style={{ perspective: 1200 }}
    >
      <motion.div
        style={{ rotateX, y, transformStyle: "preserve-3d" }}
        whileHover={{ scale: 1.02, rotateX: 0 }}
        transition={{ type: "spring", stiffness: 120, damping: 14 }}
        className="rounded-2xl border border-amber-500/30 bg-stone-900 shadow-[0_40px_80px_-20px_rgba(238,198,67,0.25)] overflow-hidden"
      >
        <img src={src} alt={alt} className="w-full block" loading="lazy" />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/70 to-transparent" />
      </motion.div>
      {caption && (
        <figcaption className="mt-3 text-center text-[11px] uppercase tracking-[0.25em] text-stone-500">
          {caption}
        </figcaption>
      )}
    </motion.figure>
  );
};

/* ---------------- scene shell ---------------- */
const Scene = ({ index, tag, title, children, persona = "both", active }) => {
  const dimmed = active === "member" && persona === "desk";
  return (
    <motion.section
      data-testid={`guide-scene-${index}`}
      className={`relative max-w-5xl mx-auto px-6 py-20 sm:py-28 transition-opacity duration-500 ${dimmed ? "opacity-40" : ""}`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: dimmed ? 0.4 : 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, ease: "easeOut" }}
    >
      <div className="flex items-center gap-4 mb-6">
        <span className="font-mono text-xs text-amber-500/80 border border-amber-500/30 rounded-full px-3 py-1">
          SCENE {String(index).padStart(2, "0")}
        </span>
        <span className="text-[10px] uppercase tracking-[0.3em] text-stone-500">{tag}</span>
        {persona === "desk" && (
          <span className="text-[10px] uppercase tracking-[0.3em] text-violet-400 border border-violet-400/30 rounded-full px-3 py-1">
            back office
          </span>
        )}
      </div>
      <h2 className="text-3xl sm:text-5xl font-bold text-stone-100 tracking-tight mb-8" style={{ fontFamily: "Georgia, serif" }}>
        <Kinetic words={title} />
      </h2>
      {children}
    </motion.section>
  );
};

const Step = ({ n, title, children }) => (
  <motion.div
    className="flex gap-4 items-start"
    initial={{ opacity: 0, x: -24 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ delay: n * 0.1, duration: 0.5 }}
  >
    <span className="flex-none w-8 h-8 rounded-full bg-amber-500 text-black font-bold text-sm flex items-center justify-center mt-0.5">
      {n}
    </span>
    <div>
      <p className="text-stone-100 font-semibold text-sm sm:text-base">{title}</p>
      <p className="text-stone-400 text-sm leading-relaxed mt-1">{children}</p>
    </div>
  </motion.div>
);

const Rail = ({ icon: Icon, title, body, delay = 0 }) => (
  <motion.div
    className="rounded-2xl border border-stone-800 bg-stone-900/60 p-5 hover:border-amber-500/50 transition-colors"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.5 }}
    whileHover={{ y: -6 }}
  >
    <Icon size={22} className="text-amber-500 mb-3" />
    <p className="text-stone-100 font-bold text-sm uppercase tracking-wider mb-2">{title}</p>
    <p className="text-stone-400 text-sm leading-relaxed">{body}</p>
  </motion.div>
);

/* ================================ PAGE ================================ */
const Guide = () => {
  const [persona, setPersona] = useState("member");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const barWidth = useSpring(scrollYProgress, { stiffness: 80, damping: 20 });
  const heroY = useTransform(scrollYProgress, [0, 0.15], [0, -80]);

  return (
    <div data-testid="guide-page" className="min-h-screen bg-[#0c0a09] text-stone-200 overflow-x-hidden">
      {/* progress bar */}
      <motion.div
        data-testid="guide-progress-bar"
        className="fixed top-0 left-0 right-0 h-1 bg-amber-500 z-50 origin-left"
        style={{ scaleX: barWidth }}
      />

      {/* ---------- HERO ---------- */}
      <div ref={heroRef} className="relative min-h-[92vh] flex items-center justify-center">
        <Particles />
        <motion.div style={{ y: heroY }} className="relative z-10 text-center px-6 max-w-4xl">
          <a href="/" data-testid="guide-back-link"
             className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 hover:text-amber-500 transition-colors mb-10">
            <ArrowLeft size={13} /> bgnzn.org
          </a>
          <p className="text-[11px] uppercase tracking-[0.4em] text-amber-500 mb-6">
            The Bourganization Institute · Chain 9940 · Interactive Viewing Manual
          </p>
          <h1 className="text-5xl sm:text-7xl font-black tracking-tight text-stone-100 leading-[0.95]" style={{ fontFamily: "Georgia, serif" }}>
            <Kinetic words="THE CASH" /> <br />
            <span className="text-amber-500"><Kinetic words="VACUUM" delay={0.25} /></span>
          </h1>
          <motion.p
            className="mt-8 text-stone-400 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }}
          >
            Watch how physical cash becomes spendable digital dollars, moves person to person
            for free inside the loop, and comes back out as bills. Every screen below is the
            live system — nothing here is a mockup.
          </motion.p>

          {/* persona toggle */}
          <motion.div
            data-testid="persona-toggle"
            className="mt-10 inline-flex rounded-full border border-stone-700 bg-stone-900/80 p-1"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.6 }}
          >
            {[
              { id: "member", label: "I'm a Member", icon: Smartphone },
              { id: "desk", label: "I Run a Desk", icon: Landmark },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                data-testid={`persona-${id}`}
                onClick={() => setPersona(id)}
                className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-all ${
                  persona === id ? "bg-amber-500 text-black" : "text-stone-400 hover:text-stone-200"
                }`}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </motion.div>

          <motion.div
            className="mt-14 flex justify-center text-stone-500"
            animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}
          >
            <ChevronDown size={22} />
          </motion.div>
        </motion.div>
      </div>

      {/* ---------- SCENE 01: three doors ---------- */}
      <Scene index={1} tag="the front door" title="Three doors. One vault.">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-5">
            <Step n={1} title="Members tap Member Wallet">
              Your cash lives at <span className="text-amber-500 font-mono text-xs">bgnzn.org/wallet</span> —
              opens with your ledger UUID and your phone number. That is the whole login.
            </Step>
            <Step n={2} title="Shops tap Merchant Register">
              <span className="text-amber-500 font-mono text-xs">bgnzn.org/register</span> turns any phone
              or tablet into a point of sale that settles in seconds, with zero card fees.
            </Step>
            <Step n={3} title="Desks tap Sovereign Portal">
              <span className="text-amber-500 font-mono text-xs">bgnzn.org/portal</span> is the back office:
              funding desk, escrow engine, liquidation console, treasury rail, proof of reserve.
            </Step>
          </div>
          <Slab src="/assets/guide/landing.jpg" alt="landing" caption="bgnzn.org — the institute's front door" />
        </div>
      </Scene>

      {/* ---------- SCENE 02: open wallet ---------- */}
      <Scene index={2} tag="member wallet" title="Your wallet is already yours.">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <Slab src="/assets/guide/wallet-dash.jpg" alt="wallet dashboard"
                caption="a real member wallet — live on chain 9940" tilt={7} />
          <div className="space-y-5">
            <Step n={1} title="Unlock with UUID + phone">
              No seed phrase taped to a monitor. The institute custodies your key inside hardware;
              ask the desk any time if you want full self-custody instead.
            </Step>
            <Step n={2} title="Read the green badge">
              LIVE · CHAIN 9940 means the balance is a token on the institute's own ledger —
              sealed by our own machines — not a number in someone's database.
            </Step>
            <Step n={3} title="Turn on Face ID">
              Tap "Enable Face ID payment approval" once, and future payments approve with your face.
            </Step>
          </div>
        </div>
      </Scene>

      {/* ---------- SCENE 03: cash in ---------- */}
      <Scene index={3} tag="cash-in" title="Cash in. Three rails, same vault.">
        <div className="grid sm:grid-cols-3 gap-5">
          <Rail icon={Landmark} title="Wire it" delay={0}
                body="From any retail bank app: open Fund your wallet, copy the wire instructions, and put your UUID in the memo line. When the wire lands, the desk settles it 1:1 — your phone shows the money the same minute." />
          <Rail icon={HandCoins} title="Hand it" delay={0.12}
                body="At a registered custody point, hand over bills. You countersign on your phone, the custodian countersigns theirs, and the sUSD lands before you leave the window." />
          <Rail icon={Store} title="Walk in with it" delay={0.24}
                body="Any approved merchant can take your cash at the counter and open a guest wallet on the spot — no membership, no paperwork, spendable immediately." />
        </div>
        <motion.p
          className="mt-8 text-center font-mono text-amber-500 text-sm tracking-widest"
          initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }} transition={{ delay: 0.3 }}
        >
          SETTLED · T+0 · 1:1 WITH ATTESTED CASH
        </motion.p>
      </Scene>

      {/* ---------- SCENE 04: P2P ---------- */}
      <Scene index={4} tag="p2p" title="Pay anyone like sending a text.">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-5">
            <Step n={1} title="They tap Receive">
              A QR and an 8-character code appear on their phone — with an amount if they set one.
            </Step>
            <Step n={2} title="You tap Scan to Pay">
              Point at their code — your regular phone camera works too. Confirm the amount.
            </Step>
            <Step n={3} title="Settled. Both get a receipt.">
              Final in seconds, on-chain, with a block number either of you can verify forever.
              Zero fees inside the loop.
            </Step>
            <p className="text-stone-500 text-xs leading-relaxed border-l-2 border-amber-500/40 pl-4">
              Prefer plain text? SMS works too: SEND 20 TO 4194945275 · BALANCE · REQUEST 15 FROM 7252765988 ·
              and text LEARN to get this whole walkthrough one lesson at a time.{" "}
              <a href="/reward" data-testid="guide-reward-link" className="text-amber-500 hover:text-amber-400 transition-colors">
                Finished the course? See your on-chain $1.00 proof →
              </a>
            </p>
          </div>
          <div className="relative flex justify-center">
            <motion.div
              className="w-56 h-56 rounded-3xl border-2 border-amber-500/50 bg-stone-900 flex items-center justify-center"
              animate={{ boxShadow: ["0 0 0 0 rgba(238,198,67,0.4)", "0 0 60px 8px rgba(238,198,67,0.15)", "0 0 0 0 rgba(238,198,67,0.4)"] }}
              transition={{ repeat: Infinity, duration: 2.4 }}
            >
              <QrCode size={120} className="text-amber-500" strokeWidth={1} />
            </motion.div>
            <motion.div
              className="absolute -right-2 top-1/2 flex items-center gap-2"
              animate={{ x: [0, 14, 0], opacity: [0.4, 1, 0.4] }}
              transition={{ repeat: Infinity, duration: 1.6 }}
            >
              <ScanLine size={22} className="text-violet-400" />
              <span className="font-mono text-xs text-violet-400">SCAN</span>
            </motion.div>
          </div>
        </div>
      </Scene>

      {/* ---------- SCENE 05: register ---------- */}
      <Scene index={5} tag="merchant register" title="Charge. Chime. Next customer.">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <Slab src="/assets/guide/register-dash.jpg" alt="register keypad"
                caption="a live register — diors dough" tilt={-7} />
          <div className="space-y-5">
            <Step n={1} title="Open the register with the shop's UUID + phone">
              Same two-field login as the wallet — the shop's own credentials.
            </Step>
            <Step n={2} title="Keypad the amount, tap CHARGE">
              A fresh QR appears for the customer. They scan, they confirm, you hear the chime.
            </Step>
            <Step n={3} title="The chime means final">
              Green SETTLED screen = the money is already in the shop's wallet on-chain.
              No processor taking 3% of your bread. "New customer" opens a guest wallet for walk-ins with cash.
            </Step>
          </div>
        </div>
      </Scene>

      {/* ---------- SCENE 06: the desk (back office) ---------- */}
      <Scene index={6} tag="sovereign portal" title="The market maker's cockpit." persona="desk" active={persona}>
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-5">
            <Step n={1} title="Funding Desk — wires become wallets">
              Incoming wires queue as declarations. Verify the bank line, tap settle, and the treasury
              settles sUSD on-chain to the member — signed inside the hardware module, receipted forever.
            </Step>
            <Step n={2} title="Smart Escrow + Forward Liquidation">
              Contracted deals approve, lock and settle on-chain. Distressed positions clear with
              enforced deposits. Every engine event carries its transaction hash.
            </Step>
            <Step n={3} title="Solvency — proof of reserve, live">
              Circulating sUSD reconciles against attested bank receipts in real time.
              Volume is welcome here. Opacity is not.
            </Step>
          </div>
          <Slab src="/assets/guide/portal-dash.jpg" alt="portal dashboard"
                caption="sovereign portal — clearing registry" tilt={6} />
        </div>
      </Scene>

      {/* ---------- SCENE 07: cash out ---------- */}
      <Scene index={7} tag="cash-out" title="The loop closes in your hand.">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-5">
            <Step n={1} title="Tap Redeem in your wallet">
              The sUSD burns on-chain the moment you confirm — the system never holds money it owes you.
            </Step>
            <Step n={2} title="Collect at a custody window">
              The custodian sees your burn, countersigns the handover, and counts out bills.
            </Step>
            <Step n={3} title="Desk-scale exits use the Treasury rail">
              Portal → Treasury Withdrawal settles any amount to any wallet, same 1:1 discipline,
              same on-chain receipt.
            </Step>
          </div>
          <div className="relative flex justify-center">
            <motion.div
              className="w-56 h-56 rounded-full border-2 border-amber-500/40 flex items-center justify-center bg-stone-900"
              animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 24, ease: "linear" }}
            >
              <div className="absolute inset-4 rounded-full border border-dashed border-amber-500/30" />
            </motion.div>
            <Banknote size={72} className="absolute text-amber-500" strokeWidth={1.2} />
          </div>
        </div>
      </Scene>

      {/* ---------- SCENE 08: the vault ---------- */}
      <Scene index={8} tag="hardware custody" title="Every key lives in silicon.">
        <div className="grid md:grid-cols-2 gap-8 items-center mb-10">
          <Slab src="/assets/guide/vault-hsm.jpg" alt="the vault"
                caption="the actual vault — yubihsm 2, rack-mounted on the institute's dell r640" tilt={5} />
          <div className="space-y-5">
            <Step n={1} title="The green glow is the point">
              That luminous module is the YubiHSM 2 — a hardware security module bolted into the
              institute's own rack. Every signature the system makes happens inside that chip.
            </Step>
            <Step n={2} title="Three keys, zero disks">
              Sealer, treasury, and legacy contract authority were generated or imported inside the
              chip. The last disk copy was shredded on camera — there is nothing left to steal
              from a hard drive, because there is nothing on one.
            </Step>
          </div>
        </div>
        <div className="rounded-3xl border border-violet-400/30 bg-gradient-to-br from-stone-900 to-[#141021] p-8 sm:p-12 text-center relative overflow-hidden">
          <Particles />
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }} transition={{ type: "spring", stiffness: 80, damping: 14 }}
            className="relative z-10"
          >
            <Cpu size={56} className="mx-auto text-violet-400 mb-6" strokeWidth={1.2} />
            <div className="grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
              {[
                ["0x0100", "SEALER", "signs every block"],
                ["0x0101", "TREASURY", "holds the float"],
                ["0x0102", "LEGACY", "governs contracts"],
              ].map(([id, name, role], i) => (
                <motion.div key={id}
                  className="rounded-xl border border-violet-400/30 bg-black/40 p-4"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                >
                  <p className="font-mono text-violet-300 text-sm">{id}</p>
                  <p className="text-stone-100 font-bold text-xs tracking-widest mt-1">{name}</p>
                  <p className="text-stone-500 text-xs mt-1">{role}</p>
                </motion.div>
              ))}
            </div>
            <p className="text-stone-300 text-lg sm:text-xl font-semibold">
              Sealer, treasury, and contract authority sign inside a hardware security module
              bolted to our own rack.
            </p>
            <p className="font-mono text-amber-500 text-sm tracking-[0.3em] mt-4">
              NOTHING ON DISK · SHREDDED 2026-10-13
            </p>
          </motion.div>
        </div>
      </Scene>

      {/* ---------- END CARD ---------- */}
      <motion.section
        data-testid="guide-end-card"
        className="relative py-28 text-center border-t border-amber-500/20"
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1 }}
      >
        <Particles />
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <p className="text-[11px] uppercase tracking-[0.4em] text-stone-500 mb-6">bgnzn.org</p>
          <h3 className="text-4xl sm:text-6xl font-black text-stone-100 leading-tight" style={{ fontFamily: "Georgia, serif" }}>
            <Kinetic words="CASH IN." /> <span className="text-amber-500"><Kinetic words="MOVE FREE." delay={0.2} /></span>{" "}
            <Kinetic words="CASH OUT." delay={0.4} />
          </h3>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <a href="/wallet" data-testid="endcard-wallet-link"
               className="rounded-full bg-amber-500 text-black font-bold text-sm uppercase tracking-wider px-7 py-3.5 hover:bg-amber-400 transition-colors">
              Member Wallet
            </a>
            <a href="/register" data-testid="endcard-register-link"
               className="rounded-full border border-amber-500/60 text-amber-500 font-bold text-sm uppercase tracking-wider px-7 py-3.5 hover:bg-amber-500/10 transition-colors">
              Merchant Register
            </a>
            <a href="/portal" data-testid="endcard-portal-link"
               className="rounded-full border border-violet-400/60 text-violet-300 font-bold text-sm uppercase tracking-wider px-7 py-3.5 hover:bg-violet-400/10 transition-colors">
              Sovereign Portal
            </a>
          </div>
          <p className="mt-10 text-stone-600 text-xs font-mono tracking-widest">
            CHAIN 9940 · SEALED IN HARDWARE · THE BOURGANIZATION INSTITUTE
          </p>
        </div>
      </motion.section>
    </div>
  );
};

export default Guide;
